<svelte:options customElement={{ tag: "bt-learning-history-wrapper", shadow: "none" }} />

<script lang="ts">
  import { onMount } from 'svelte';
	import { writable } from 'svelte/store';
  // External imports
  import { 
    createSvelteTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender
  } from '@tanstack/svelte-table';
  import type {
    ColumnDef,
    TableOptions,
    OnChangeFn,
    SortingState,
    RowSelectionState,
    PaginationState,
  } from '@tanstack/svelte-table';
  // Other components
  import LearningHistoryTitleCell from './LearningHistoryTitleCell.svelte';
  import LearningHistoryProgressCell from './LearningHistoryProgressCell.svelte';
  import Icon from '$shared/icons/Icon.svelte';
  import ExpertLockup from './ExpertLockup.svelte';
  import { userTableTooltips } from '$comp/user-management/userManagementConfig';
  import DataTable from '$lib/components/shared/data-table/DataTable.svelte';

  export let loadingText = "Loading your learning history…";
  export let loadingMoreText = "Loading more…";
  export let searchLabel = "Search by title";
  export let titleHeader = "Title";
  export let contentTypeHeader = "Content type";
  export let expertHeader = "Expert";
  export let durationHeader = "Duration";
  export let progressHeader = "Progress";
  export let lastViewedHeader = "Last viewed";
  export let pageText = "Page {current} of {total}";
  export let previousText = "Prev";
  export let nextText = "Next";
  export let locale = "en";

  // Formats a unix timestamp as a short date in the user's language, e.g. "Oct 22, 2026" or "22 oct 2026" in Spanish
  const formatLastViewed = (seconds: number) => {
    const date = new Date(seconds * 1000);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
    try {
      return date.toLocaleDateString(locale, options);
    } catch {
      return date.toLocaleDateString('en', options);
    }
  };

  type User = {
    email: string
    expires_at: string
    full_name: string
    id: number
    roles: any[]
    status: string
    updated_at: string
    completed: boolean;
  }

  const defaultColumns: ColumnDef<User>[] = [
    {
      accessorKey: 'id',
      header: "ID",
      enableGlobalFilter: false
    },
    {
      accessorKey: 'title',
      header: titleHeader,
      cell: (info) => {
        const original: any = info.row.original;
        return flexRender(LearningHistoryTitleCell, {
          title: info.getValue(),
          url: original.url
        });
      }
    },
    {
      accessorKey: 'content_type',
      header: () => contentTypeHeader,
    },
    {
      accessorKey: 'expert_name',
      header: expertHeader,
      cell: (info) => {
        return flexRender(ExpertLockup, {image: info.row.getValue('expert_img')?.url, name: info.getValue()})
      },
      enableGlobalFilter: false,
    },
    {
      accessorKey: 'expert_img',
      enableGlobalFilter: false
    },
    {
      accessorKey: 'duration_seconds',
      enableGlobalFilter: false
    },
    {
      accessorKey: 'duration',
      header: durationHeader,
      sortingFn: (rowA, rowB, _columnId) => {
        return rowA.getValue('duration_seconds') - rowB.getValue('duration_seconds')
      },
      enableGlobalFilter: false
    },
    {
      accessorKey: 'progress',
      header: progressHeader,
      cell: (info) => {
        const original = info.row.original;
        if (original.completed) {
          return flexRender(Icon, {icon: 'CheckCircleSolid', size: 'medium', iconClass: 'fill-green-400'})
        } else {
          return flexRender(LearningHistoryProgressCell, {percent: info.getValue()})
        }
      },
      enableGlobalFilter: false
    },
    {
      accessorKey: 'last_viewed',
      enableGlobalFilter: false
    },
    {
      accessorKey: 'last_viewed_display',
      header: lastViewedHeader,
      cell: (info) => formatLastViewed(info.row.getValue('last_viewed')),
      sortingFn: (rowA, rowB, _columnId) => {
        return rowA.getValue('last_viewed') - rowB.getValue('last_viewed')
      },
      enableGlobalFilter: false
    }
  ]

  // Tanstack Table plugin states
  let sorting: SortingState = [{ id: 'last_viewed', desc: true }];
  let rowSelection: RowSelectionState = [];

  // Custum server-side pagination state
  let pagination: PaginationState = { pageIndex: 0, pageSize: 10 };
  let isLoading = true;
  let totalPages = 1;

  const defaultData: any[] = [];
  let allRows: any[] = [];
  let loadedPages = new Set<number>();
  let hasAllDataLoaded = false;
  let hasStartedBackgroundLoad = false;

  const setSorting: OnChangeFn<SortingState> = (updater) => {
    if (updater instanceof Function) {
      sorting = updater(sorting);
    } else {
      sorting = updater;
    }
    options.update((old) => ({
      ...old,
      state: {
        ...old.state,
        sorting
      }
    }));
  };

  const setPagination: OnChangeFn<PaginationState> = (updater) => {
    if (updater instanceof Function) {
      pagination = updater(pagination);
    } else {
      pagination = updater;
    }
    options.update((old) => ({
      ...old,
      state: {
        ...old.state,
        pagination
      }
    }));
  };

  const options = writable<TableOptions<User>>({
    data: defaultData,
    columns: defaultColumns,
    state: {
      columnVisibility: {
        'id': false,
        'last_viewed': false,
        'expert_img': false,
        'duration_seconds': false,
      },
      sorting,
      rowSelection,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const fetchAndRender = async (pageIndex = 0, pageSize = 10) => {
    isLoading = true;

    const page = pageIndex + 1;
    const perPage = pageSize;

    const url = new URL(window.location.origin + '/dashboard/history');
    url.searchParams.set('page', page.toString());
    url.searchParams.set('per_page', perPage.toString());

    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' }
      });

      const json = await response.json();
      const rows = json.rows || [];
      totalPages = json.total_pages || 1;

      if (totalPages <= 1) {
        hasAllDataLoaded = true;
      }

      loadedPages.add(page);

      options.update((opts) => ({
        ...opts,
        data: rows
      }));

      allRows = [...allRows, ...rows];

      // Start background loading (only once, after first page)
      if (!hasStartedBackgroundLoad && totalPages > 1) {
        hasStartedBackgroundLoad = true;
        preloadRemainingPages(perPage);
      }
    } finally {
      isLoading = false;
    }
  };

  const preloadRemainingPages = async (perPage: number) => {
    if (hasAllDataLoaded) return;

    // Build a list of page numbers we still need
    const pagesToLoad: number[] = [];
    for (let page = 1; page <= totalPages; page++) {
      if (!loadedPages.has(page)) {
        pagesToLoad.push(page);
      }
    }

    // If there's nothing left to do
    if (pagesToLoad.length === 0) {
      hasAllDataLoaded = true;
      return;
    }

    // Helper to fetch a single page and return its rows
    const fetchPageRows = async (page: number): Promise<any[]> => {
      const url = new URL(window.location.origin + '/dashboard/history');
      url.searchParams.set('page', page.toString());
      url.searchParams.set('per_page', perPage.toString());

      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' }
        });

        const json = await response.json();
        const rows = json.rows || [];

        loadedPages.add(page);
        return rows;
      } catch (e) {
        console.error('Error preloading history page', page, e);
        return [];
      }
    };

    // Fire all remaining page requests in parallel
    const results = await Promise.all(
      pagesToLoad.map((page) => fetchPageRows(page))
    );

    // Flatten new rows
    const newRowsFromPages = results.flat();

    allRows = [...allRows, ...newRowsFromPages];

    hasAllDataLoaded = true;

    // Switch table to full dataset → global search works again
    options.update((opts) => ({
      ...opts,
      data: allRows,
      state: {
        ...opts.state,
        pagination: { pageIndex: 0, pageSize: 10 }, // reset to first page
      }
    }));
  };

  const table = createSvelteTable(options);

  onMount(async () => {
    await fetchAndRender(pagination.pageIndex, pagination.pageSize);
  });
</script>

<div class="p-2">
	{#if isLoading}
    <div class="text-sm text-neutral-500">
      {loadingText}
    </div>
  {:else}
    <DataTable
      table={table}
      tableName=""
      tooltips={userTableTooltips}
      theme="frontend"
      canFilter
      filterLabel={searchLabel}
      {pageText}
      {previousText}
      {nextText}
    >
    </DataTable>
    {#if !hasAllDataLoaded}
      <div class="mt-4 text-sm text-neutral-500 text-center">
        {loadingMoreText}
      </div>
    {/if}
  {/if}
</div>
